import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  UserOutlined, ArrowLeftOutlined, EyeOutlined,
  ClockCircleOutlined, FacebookOutlined, TwitterOutlined, LinkedinOutlined, LinkOutlined
} from '@ant-design/icons';
import { Spin, message, Tag } from 'antd';
import { getBlogPostBySlug } from '../api/blogPostApi';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setNotFound(false);
        const response = await getBlogPostBySlug(slug);
        if (response?.success && response.blogPost) {
          setPost(response.blogPost);
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error('Error fetching blog post:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDay = (dateString) => {
    if (!dateString) return { day: '--', month: '' };
    const d = new Date(dateString);
    return {
      day: d.getDate(),
      month: d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()
    };
  };

  const estimateReadTime = (html) => {
    if (!html) return 1;
    const text = html.replace(/<[^>]*>/g, ' ');
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 200));
  };

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleCopyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(currentUrl);
      message.success('Link copied to clipboard');
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Rubik', sans-serif",
        padding: 40
      }}>
        <h2 style={{ color: '#470172', marginBottom: 12 }}>Blog post not found</h2>
        <p style={{ color: '#666', marginBottom: 24 }}>This post may have been unpublished or removed.</p>
        <Link
          to="/blog"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            color: '#fff',
            background: '#470172',
            padding: '10px 24px',
            borderRadius: 8,
            textDecoration: 'none',
            fontWeight: 600
          }}
        >
          <ArrowLeftOutlined /> Back to Blog
        </Link>
      </div>
    );
  }

  const readTime = estimateReadTime(post.content);
  const { day, month } = formatDay(post.publishedAt);

  return (
    <div style={{ fontFamily: "'Rubik', sans-serif", backgroundColor: '#fafbfc', minHeight: '100vh', paddingBottom: 80, overflowX: 'hidden' }}>
      <style>{`
        .blogdetail-wrap { max-width: 1200px; margin: 0 auto; padding: 30px 32px 0; }
        .blogdetail-back-btn {
          display: inline-flex; align-items: center; gap: 8px;
          background: transparent; border: none; color: #470172;
          font-size: 15px; font-weight: 600; cursor: pointer; padding: 0; margin-bottom: 24px;
          outline: none; -webkit-tap-highlight-color: transparent; box-shadow: none;
          max-width: fit-content;
        }
        .blogdetail-back-btn::-moz-focus-inner { border: 0; }
        .blogdetail-back-btn:focus, .blogdetail-back-btn:focus-visible { outline: none; box-shadow: none; }
        .blogdetail-title { font-size: 36px; font-weight: 700; color: #1a202c; line-height: 1.3; margin: 0 0 28px; }

        .blogdetail-layout { display: grid; grid-template-columns: 1fr 340px; gap: 32px; align-items: start; }

        .blogdetail-card {
          background: #fff; border: 1px solid #eee; border-radius: 16px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.04); padding: 28px;
        }

        .blogdetail-tags-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px; }
        .blogdetail-cover-img { width: 100%; max-height: 420px; object-fit: cover; border-radius: 12px; margin-bottom: 20px; display: block; }

        .blogdetail-content p { margin-bottom: 20px; }
        .blogdetail-content img { max-width: 100%; height: auto; border-radius: 10px; margin: 24px 0; }
        .blogdetail-content h2, .blogdetail-content h3 {
          font-weight: 700; color: #1a202c; text-transform: uppercase; letter-spacing: 0.5px;
          position: relative; padding-bottom: 12px;
        }
        .blogdetail-content h2 { font-size: 22px; margin: 40px 0 20px; }
        .blogdetail-content h3 { font-size: 18px; margin: 32px 0 18px; }
        .blogdetail-content h2::after, .blogdetail-content h3::after {
          content: ''; position: absolute; left: 0; bottom: 0; width: 36px; height: 3px;
          background: #470172; border-radius: 2px;
        }
        .blogdetail-content a { color: #470172; font-weight: 600; }
        .blogdetail-content blockquote {
          position: relative;
          background: #f3e8ff;
          padding: 30px 32px 30px 70px; margin: 28px 0; color: #2d1f38; font-style: italic; font-size: 18px;
          border-radius: 12px; border: none; line-height: 1.7; text-align: center;
        }
        .blogdetail-content blockquote::before {
          content: '\\201C'; position: absolute; top: 8px; left: 24px;
          font-size: 56px; font-weight: 700; color: #d3b8ec; font-style: normal; line-height: 1;
          font-family: Georgia, serif;
        }
        .blogdetail-content ul, .blogdetail-content ol { padding-left: 26px; margin-bottom: 20px; }

        /* Sidebar */
        .blogdetail-sidebar { position: sticky; top: 24px; display: flex; flex-direction: column; gap: 20px; }
        .blogdetail-date-badge {
          display: flex; align-items: center; gap: 14px; margin-bottom: 20px;
        }
        .blogdetail-date-box {
          background: #470172; color: #fff; border-radius: 10px; width: 56px; height: 56px;
          display: flex; flex-direction: column; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .blogdetail-date-box .day { font-size: 20px; font-weight: 700; line-height: 1.1; }
        .blogdetail-date-box .month { font-size: 11px; font-weight: 600; letter-spacing: 0.5px; }
        .blogdetail-date-label { font-size: 11px; color: #999; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; }
        .blogdetail-date-full { font-size: 15px; color: #1a202c; font-weight: 600; }

        .blogdetail-info-row {
          display: flex; align-items: flex-start; gap: 12px;
          padding: 14px 0; border-top: 1px solid #f0f0f0;
        }
        .blogdetail-info-row .icon {
          width: 32px; height: 32px; border-radius: 8px; background: #f3e8ff; color: #470172;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 14px;
        }
        .blogdetail-info-label { font-size: 11px; color: #999; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; margin-bottom: 2px; }
        .blogdetail-info-value { font-size: 14px; color: #1a202c; font-weight: 600; }

        .blogdetail-share-label { font-size: 13px; font-weight: 700; color: #1a202c; margin-bottom: 12px; }
        .blogdetail-share-row { display: flex; gap: 10px; flex-wrap: wrap; }
        .blogdetail-share-btn {
          width: 38px; height: 38px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
          background: #f3e8ff; color: #470172; border: none; cursor: pointer; text-decoration: none; font-size: 15px;
          transition: all 0.25s ease;
        }
        .blogdetail-share-btn:hover { background: #470172; color: #fff; }

        @media (max-width: 992px) {
          .blogdetail-layout { grid-template-columns: 1fr; }
          .blogdetail-sidebar { position: static; }
        }

        @media (max-width: 768px) {
          .blogdetail-wrap { padding: 20px 16px 0; }
          .blogdetail-title { font-size: 24px; text-align: center; }
          .blogdetail-card { padding: 18px; }
          .blogdetail-cover-img { max-height: 220px; }
          .blogdetail-content { font-size: 15px !important; text-align: center; }
          .blogdetail-content ul, .blogdetail-content ol { text-align: left; display: inline-block; }
          .blogdetail-content h2::after, .blogdetail-content h3::after { left: 50%; transform: translateX(-50%); }
          .blogdetail-content blockquote { font-size: 16px; padding: 24px 20px 24px 56px; }
          .blogdetail-content blockquote::before { font-size: 42px; left: 16px; }
          .blogdetail-tags-row { justify-content: center; }
          .blogdetail-date-badge { justify-content: center; }
          .blogdetail-share-row { justify-content: center; }
        }
      `}</style>

      <div className="blogdetail-wrap">
        <button type="button" className="blogdetail-back-btn" onClick={() => navigate('/blog')}>
          <ArrowLeftOutlined /> Back to Blog
        </button>

        <h1 className="blogdetail-title">{post.title}</h1>

        <div className="blogdetail-layout">
          {/* Main content */}
          <div className="blogdetail-card">
            {Array.isArray(post.tags) && post.tags.length > 0 && (
              <div className="blogdetail-tags-row">
                {post.tags.map((tag) => (
                  <Tag
                    key={tag}
                    style={{
                      background: '#f3e8ff',
                      color: '#470172',
                      border: 'none',
                      borderRadius: 20,
                      padding: '4px 14px',
                      fontSize: 13,
                      fontWeight: 600,
                      margin: 0
                    }}
                  >
                    {tag}
                  </Tag>
                ))}
              </div>
            )}

            <img src={post.coverImage} alt={post.title} className="blogdetail-cover-img" />

            {/* Content authored via the admin panel's rich-text editor — admin
                access is server-enforced (auth + admin middleware), so this
                HTML is trusted, same as other admin-authored content on the
                site. */}
            <div
              className="blogdetail-content"
              style={{ fontSize: 17, lineHeight: 1.9, color: '#2d3748' }}
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>

          {/* Sidebar */}
          <div className="blogdetail-sidebar">
            <div className="blogdetail-card">
              <div className="blogdetail-date-badge">
                <div className="blogdetail-date-box">
                  <span className="day">{day}</span>
                  <span className="month">{month}</span>
                </div>
                <div>
                  <div className="blogdetail-date-label">Published</div>
                  <div className="blogdetail-date-full">{formatDate(post.publishedAt)}</div>
                </div>
              </div>

              <div className="blogdetail-info-row">
                <div className="icon"><ClockCircleOutlined /></div>
                <div>
                  <div className="blogdetail-info-label">Read Time</div>
                  <div className="blogdetail-info-value">{readTime} min read</div>
                </div>
              </div>

              <div className="blogdetail-info-row">
                <div className="icon"><EyeOutlined /></div>
                <div>
                  <div className="blogdetail-info-label">Views</div>
                  <div className="blogdetail-info-value">{post.views || 0}</div>
                </div>
              </div>

              <div className="blogdetail-info-row">
                <div className="icon">
                  {post.authorImage ? (
                    <img
                      src={post.authorImage}
                      alt={post.author || 'Author'}
                      style={{ width: '100%', height: '100%', borderRadius: 8, objectFit: 'cover' }}
                    />
                  ) : (
                    <UserOutlined />
                  )}
                </div>
                <div>
                  <div className="blogdetail-info-label">Written By</div>
                  <div className="blogdetail-info-value">{post.author || 'AID Team'}</div>
                </div>
              </div>
            </div>

            {/* <div className="blogdetail-card">
              <div className="blogdetail-share-label">Share this article</div>
              <div className="blogdetail-share-row">
                <a
                  className="blogdetail-share-btn"
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on Facebook"
                >
                  <FacebookOutlined />
                </a>
                <a
                  className="blogdetail-share-btn"
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on Twitter"
                >
                  <TwitterOutlined />
                </a>
                <a
                  className="blogdetail-share-btn"
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on LinkedIn"
                >
                  <LinkedinOutlined />
                </a>
                <button type="button" className="blogdetail-share-btn" onClick={handleCopyLink} aria-label="Copy link">
                  <LinkOutlined />
                </button>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetailPage;
